import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  auth,
  db,
  getNamedFirebaseApp,
  serverTimestamp,
  firebaseConfig as dashboardFirebaseConfig,
} from '../firebase/firebaseConfig';
import {
  ALL_MODULES,
  EMPTY_MODULES,
  MODULES,
  ROLES,
  hasAccess as hasModuleAccess,
  normalizeModules,
  normalizeRole,
} from '../constants/rbac';
import { isVehicleReadyForAssignment } from '../utils/helpers';

const AdminContext = createContext();
const USER_CREATOR_APP = 'rallys-dashboard-user-creator';

const ACCESS_MAP = {
  drivers: ['dashboard', 'drivers', 'driverSchedule', 'inspection', 'tasks', 'driverDetails', 'chatSupport'],
  vehicles: ['dashboard', 'drivers', 'garage', 'tasks', 'inspection'],
  tasks: ['dashboard', 'tasks', 'driverDetails'],
  availability: ['driverSchedule'],
  inspections: ['inspection'],
};

const PROFILE_CHANGE_COOLDOWN_HOURS = 48;
const PROFILE_CHANGE_COOLDOWN_MS = PROFILE_CHANGE_COOLDOWN_HOURS * 60 * 60 * 1000;

const DRIVER_NON_PROFILE_UPDATE_FIELDS = new Set([
  'updatedAt',
  'createdAt',
  'availabilitySchedule',
  'assignedVehicle',
  'approved',
  'status',
  'rejectionReason',
  'rejectedAt',
  'approvedAt',
  'fcmToken',
  'fcmTokenUpdatedAt',
  'userId',
  'createdByAdmin',
  'createdByAdminUid',
  'createdByAdminEmail',
  'authError',
  'profileLastChangedAt',
  'lastProfileChangeAt',
  'isLocked',
  'lockReason',
  'lockDocument',
  'lockCategory',
  'lockedAt',
  'lockedByUid',
  'lockedByEmail',
  'unlockedAt',
  'unlockedByUid',
  'unlockedByEmail',
]);

const STAFF_NON_PROFILE_UPDATE_FIELDS = new Set([
  'updatedAt',
  'createdAt',
  'availabilitySchedule',
  'profileLastChangedAt',
  'lastProfileChangeAt',
]);

const parseTimestampToDate = (value) => {
  if (!value) {
    return null;
  }

  if (value?.toDate && typeof value.toDate === 'function') {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isProfileFieldUpdate = (data = {}, nonProfileFields) =>
  Object.keys(data).some((fieldKey) => !nonProfileFields.has(fieldKey));

const buildProfileCooldownMessage = (entityLabel, hoursRemaining) =>
  `${entityLabel} profile can only be changed once every ${PROFILE_CHANGE_COOLDOWN_HOURS} hours. Please try again in about ${hoursRemaining} hour(s).`;

const DASHBOARD_FUNCTIONS_BASE_URL = `https://us-central1-${dashboardFirebaseConfig.projectId}.cloudfunctions.net`;

const parsePasswordResetResponse = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

const callPasswordResetEndpoint = async (endpoint, payload) => {
  const response = await fetch(`${DASHBOARD_FUNCTIONS_BASE_URL}/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const data = await parsePasswordResetResponse(response);

  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || data?.message || 'Password reset request failed');
  }

  return data;
};

const normalizeDashboardUser = (userId, data = {}, fallback = {}) => {
  const role = normalizeRole(data.role) || normalizeRole(fallback.role);
  return {
    id: userId,
    uid: userId,
    name: data.name || fallback.name || '',
    email: data.email || fallback.email || '',
    photoURL: data.photoURL || fallback.photoURL || null,
    role,
    modules: role ? normalizeModules(role, data.modules || fallback.modules) : { ...EMPTY_MODULES },
    availabilitySchedule: Array.isArray(data.availabilitySchedule)
      ? data.availabilitySchedule
      : Array.isArray(fallback.availabilitySchedule)
        ? fallback.availabilitySchedule
        : [],
    createdAt: data.createdAt ?? fallback.createdAt ?? null,
    updatedAt: data.updatedAt ?? fallback.updatedAt ?? null,
  };
};

const buildAdminProfile = (firebaseUser, adminData = {}) => ({
  name: adminData.name || firebaseUser.displayName || firebaseUser.email || 'Admin User',
  email: firebaseUser.email || adminData.email || '',
  role: ROLES.ADMIN,
  modules: { ...ALL_MODULES },
  createdAt: adminData.createdAt ?? serverTimestamp(),
  updatedAt: serverTimestamp(),
});

const canReadAny = (user, moduleKeys) => moduleKeys.some((moduleKey) => hasModuleAccess(user, moduleKey));

const sortUsers = (users) => {
  const order = { [ROLES.ADMIN]: 0, [ROLES.MANAGER]: 1, [ROLES.DISPATCH]: 2 };
  return [...users].sort((firstUser, secondUser) => {
    const firstOrder = order[firstUser.role] ?? 99;
    const secondOrder = order[secondUser.role] ?? 99;
    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder;
    }
    return (firstUser.name || firstUser.email || '').localeCompare(secondUser.name || secondUser.email || '');
  });
};

const subscribeToCollection = (enabled, collectionName, setter) => {
  if (!enabled) {
    setter([]);
    return undefined;
  }

  return onSnapshot(
    collection(db, collectionName),
    (snapshot) => {
      setter(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    },
    (error) => {
      console.error(`Failed to listen to ${collectionName}:`, error);
      setter([]);
    }
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [dashboardUsers, setDashboardUsers] = useState([]);

  const user =
    authUser && userProfile
      ? {
          uid: authUser.uid,
          email: userProfile.email || authUser.email || '',
          photoURL: userProfile.photoURL || authUser.photoURL || null,
          displayName: authUser.displayName || userProfile.name || '',
          name: userProfile.name || authUser.displayName || authUser.email || 'Dashboard User',
          availabilitySchedule: Array.isArray(userProfile.availabilitySchedule)
            ? userProfile.availabilitySchedule
            : [],
          role: userProfile.role,
          modules: userProfile.modules,
          createdAt: userProfile.createdAt,
          updatedAt: userProfile.updatedAt,
        }
      : null;

  const isAdmin = user?.role === ROLES.ADMIN;

  const hasAccess = (candidate, moduleKey) => {
    if (typeof candidate === 'string') {
      return hasModuleAccess(user, candidate);
    }
    return hasModuleAccess(candidate, moduleKey);
  };

  const canReadDriversData = Boolean(authUser && userProfile && canReadAny(userProfile, ACCESS_MAP.drivers));
  const canReadVehiclesData = Boolean(authUser && userProfile && canReadAny(userProfile, ACCESS_MAP.vehicles));
  const canReadTasksData = Boolean(authUser && userProfile && canReadAny(userProfile, ACCESS_MAP.tasks));
  const canReadAvailabilityData = Boolean(authUser && userProfile && canReadAny(userProfile, ACCESS_MAP.availability));
  const canReadInspectionsData = Boolean(authUser && userProfile && canReadAny(userProfile, ACCESS_MAP.inspections));
  const canReadDashboardUsersData =
    Boolean(authUser && userProfile) &&
    (
      hasModuleAccess(userProfile, 'createUser') ||
      hasModuleAccess(userProfile, 'driverSchedule') ||
      hasModuleAccess(userProfile, 'staffSchedule')
    );

  const createAuthUserAccount = async (email, password) => {
    const secondaryAuth = getAuth(getNamedFirebaseApp(USER_CREATOR_APP));
    try {
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      return userCredential.user;
    } finally {
      try {
        await firebaseSignOut(secondaryAuth);
      } catch (error) {
        console.warn('Secondary auth sign-out skipped:', error);
      }
    }
  };

  const ensureUserProfile = async (firebaseUser) => {
    const userRef = doc(db, 'users', firebaseUser.uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const normalizedUser = normalizeDashboardUser(firebaseUser.uid, userSnap.data(), {
        email: firebaseUser.email || '',
        name: firebaseUser.displayName || firebaseUser.email || 'Dashboard User',
      });

      if (normalizedUser.role) {
        const snapshotData = userSnap.data();
        const updates = {};
        if (normalizedUser.name && snapshotData.name !== normalizedUser.name) {
          updates.name = normalizedUser.name;
        }
        if (normalizedUser.email && snapshotData.email !== normalizedUser.email) {
          updates.email = normalizedUser.email;
        }
        if (normalizedUser.role === ROLES.ADMIN) {
          const missingModule = MODULES.some((moduleKey) => snapshotData.modules?.[moduleKey] !== true);
          if (missingModule) {
            updates.modules = { ...ALL_MODULES };
          }
        }
        if (Object.keys(updates).length > 0) {
          updates.updatedAt = serverTimestamp();
          await setDoc(userRef, updates, { merge: true });
        }
        return normalizedUser;
      }
    }

    const adminSnap = await getDoc(doc(db, 'admins', firebaseUser.uid));
    if (adminSnap.exists() && adminSnap.data().admin === true) {
      const adminProfile = buildAdminProfile(firebaseUser, adminSnap.data());
      await setDoc(userRef, adminProfile, { merge: true });
      return normalizeDashboardUser(firebaseUser.uid, adminProfile, adminProfile);
    }

    return null;
  };

  useEffect(() => {
    let unsubscribeProfile = () => {};

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      unsubscribeProfile();
      setAuthUser(firebaseUser);

      if (!firebaseUser) {
        setUserProfile(null);
        setDrivers([]);
        setVehicles([]);
        setTasks([]);
        setAvailability([]);
        setInspections([]);
        setDashboardUsers([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const resolvedProfile = await ensureUserProfile(firebaseUser);
        if (!resolvedProfile) {
          await firebaseSignOut(auth);
          setUserProfile(null);
          setLoading(false);
          return;
        }

        setUserProfile(resolvedProfile);

        unsubscribeProfile = onSnapshot(
          doc(db, 'users', firebaseUser.uid),
          async (snapshot) => {
            if (snapshot.exists()) {
              setUserProfile(
                normalizeDashboardUser(firebaseUser.uid, snapshot.data(), {
                  email: firebaseUser.email || '',
                  name: firebaseUser.displayName || firebaseUser.email || 'Dashboard User',
                })
              );
              setLoading(false);
              return;
            }

            const fallbackProfile = await ensureUserProfile(firebaseUser);
            if (!fallbackProfile) {
              setUserProfile(null);
              await firebaseSignOut(auth);
            } else {
              setUserProfile(fallbackProfile);
            }
            setLoading(false);
          },
          (error) => {
            console.error('Failed to listen to current user permissions:', error);
            setLoading(false);
          }
        );
      } catch (error) {
        console.error('Error resolving dashboard user profile:', error);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeProfile();
      unsubscribeAuth();
    };
  }, []);

  useEffect(
    () => subscribeToCollection(canReadDriversData, 'drivers', setDrivers),
    [authUser?.uid, canReadDriversData]
  );
  useEffect(
    () => subscribeToCollection(canReadVehiclesData, 'warehouse', setVehicles),
    [authUser?.uid, canReadVehiclesData]
  );
  useEffect(
    () => subscribeToCollection(canReadTasksData, 'tasks', setTasks),
    [authUser?.uid, canReadTasksData]
  );
  useEffect(
    () => subscribeToCollection(canReadAvailabilityData, 'availability', setAvailability),
    [authUser?.uid, canReadAvailabilityData]
  );
  useEffect(
    () => subscribeToCollection(canReadInspectionsData, 'inspections', setInspections),
    [authUser?.uid, canReadInspectionsData]
  );

  useEffect(() => {
    if (!canReadDashboardUsersData) {
      setDashboardUsers([]);
      return undefined;
    }

    return onSnapshot(
      collection(db, 'users'),
      (snapshot) => {
        const nextUsers = snapshot.docs
          .map((item) =>
            normalizeDashboardUser(item.id, item.data(), {
              email: item.data().email || '',
              name: item.data().name || '',
            })
          )
          .filter((dashboardUser) => dashboardUser.role);
        setDashboardUsers(sortUsers(nextUsers));
      },
      (error) => {
        console.error('Failed to listen to users collection:', error);
        setDashboardUsers([]);
      }
    );
  }, [authUser?.uid, canReadDashboardUsersData]);

  const signIn = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const resolvedProfile = await ensureUserProfile(result.user);
      if (!resolvedProfile) {
        await firebaseSignOut(auth);
        return { success: false, error: 'Access denied. No dashboard role is assigned to this account.' };
      }
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const requestPasswordResetOtp = async (email) => {
    try {
      const result = await callPasswordResetEndpoint('requestPasswordResetOtp', { email });
      return { success: true, message: result.message };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: error.message };
    }
  };

  const verifyPasswordResetOtp = async (email, otp, newPassword) => {
    try {
      const result = await callPasswordResetEndpoint('verifyPasswordResetOtp', {
        email,
        otp,
        newPassword,
      });
      return { success: true, message: result.message };
    } catch (error) {
      console.error('Password reset verification error:', error);
      return { success: false, error: error.message };
    }
  };

  const sendPasswordResetEmail = requestPasswordResetOtp;

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const createDashboardUser = async ({ name, email, password, role, modules = EMPTY_MODULES, photoURL = '' }) => {
    try {
      if (!hasModuleAccess(user, 'createUser')) {
        return { success: false, error: 'You do not have permission to create dashboard users.' };
      }

      const normalizedRole = normalizeRole(role);
      if (!normalizedRole) {
        return { success: false, error: 'Please choose a valid role.' };
      }
      if (!password || password.length < 6) {
        return { success: false, error: 'Password must be at least 6 characters long.' };
      }

      const createdAuthUser = await createAuthUserAccount(email, password);
      await setDoc(doc(db, 'users', createdAuthUser.uid), {
        name: name.trim(),
        email: email.trim(),
        photoURL: photoURL || '',
        role: normalizedRole,
        modules: normalizeModules(normalizedRole, modules),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (normalizedRole === ROLES.ADMIN) {
        await setDoc(
          doc(db, 'admins', createdAuthUser.uid),
          { admin: true, name: name.trim(), email: email.trim(), updatedAt: serverTimestamp() },
          { merge: true }
        );
      }

      return { success: true, id: createdAuthUser.uid };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const saveUserPermissions = async (userId, role, modules) => {
    try {
      const canManageUsers = hasModuleAccess(user, 'createUser');
      if (!canManageUsers) {
        return { success: false, error: 'You do not have permission to update permissions.' };
      }

      const normalizedRole = normalizeRole(role);
      if (!normalizedRole) {
        return { success: false, error: 'Invalid role selected.' };
      }

      const userRef = doc(db, 'users', userId);
      const userSnapshot = await getDoc(userRef);
      const userData = userSnapshot.exists() ? userSnapshot.data() : {};
      const targetRole = normalizedRole;

      const shouldEnforceCooldown = !canManageUsers;

      if (shouldEnforceCooldown && (targetRole === ROLES.MANAGER || targetRole === ROLES.DISPATCH)) {
        const lastProfileChange = parseTimestampToDate(
          userData.profileLastChangedAt || userData.lastProfileChangeAt
        );

        if (lastProfileChange) {
          const nextAllowedChangeAt =
            lastProfileChange.getTime() + PROFILE_CHANGE_COOLDOWN_MS;

          if (Date.now() < nextAllowedChangeAt) {
            const hoursRemaining = Math.ceil(
              (nextAllowedChangeAt - Date.now()) / (60 * 60 * 1000)
            );
            return {
              success: false,
              error: buildProfileCooldownMessage('Staff', hoursRemaining),
            };
          }
        }
      }

      await setDoc(
        userRef,
        {
          role: normalizedRole,
          modules: normalizeModules(normalizedRole, modules),
          updatedAt: serverTimestamp(),
          ...(targetRole === ROLES.MANAGER || targetRole === ROLES.DISPATCH
            ? { profileLastChangedAt: serverTimestamp() }
            : {}),
        },
        { merge: true }
      );

      if (normalizedRole === ROLES.ADMIN) {
        await setDoc(doc(db, 'admins', userId), { admin: true, updatedAt: serverTimestamp() }, { merge: true });
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateDashboardUser = async (userId, data) => {
    try {
      const canManageUsers = hasModuleAccess(user, 'createUser');
      const isStaffSelfServiceRole =
        user?.role === ROLES.MANAGER || user?.role === ROLES.DISPATCH;
      const canManageAllStaffSchedules =
        canManageUsers ||
        hasModuleAccess(user, 'driverSchedule') ||
        hasModuleAccess(user, 'staffSchedule');
      const canManageOwnStaffSchedule = isStaffSelfServiceRole;

      if (!canManageUsers && !canManageAllStaffSchedules && !canManageOwnStaffSchedule) {
        return { success: false, error: 'You do not have permission to update staff users.' };
      }

      const isSelfUpdate = Boolean(user?.uid && userId === user.uid);
      const canStaffSelfEditProfile = !canManageUsers && canManageOwnStaffSchedule && isSelfUpdate;

      const allowedFields = canManageUsers || canStaffSelfEditProfile
        ? new Set(['availabilitySchedule', 'name', 'email', 'photoURL'])
        : new Set(['availabilitySchedule']);

      const invalidField = Object.keys(data || {}).find(
        (fieldKey) => !allowedFields.has(fieldKey)
      );
      if (invalidField) {
        return {
          success: false,
          error: canManageUsers
            ? `You cannot update ${invalidField}.`
            : 'You can only update availability schedule.',
        };
      }

      if (!canManageUsers && !canManageAllStaffSchedules) {
        if (!user?.uid || userId !== user.uid) {
          return { success: false, error: 'You can only update your own schedule.' };
        }
      }

      const isStaffProfileUpdate = isProfileFieldUpdate(
        data,
        STAFF_NON_PROFILE_UPDATE_FIELDS
      );
      const shouldEnforceCooldown = !canManageUsers;
      const userRef = doc(db, 'users', userId);

      if (shouldEnforceCooldown && isStaffProfileUpdate) {
        const userSnapshot = await getDoc(userRef);
        const userData = userSnapshot.exists() ? userSnapshot.data() : {};
        const role = normalizeRole(userData.role);

        if (role === ROLES.MANAGER || role === ROLES.DISPATCH) {
          const lastProfileChange = parseTimestampToDate(
            userData.profileLastChangedAt || userData.lastProfileChangeAt
          );

          if (lastProfileChange) {
            const nextAllowedChangeAt =
              lastProfileChange.getTime() + PROFILE_CHANGE_COOLDOWN_MS;

            if (Date.now() < nextAllowedChangeAt) {
              const hoursRemaining = Math.ceil(
                (nextAllowedChangeAt - Date.now()) / (60 * 60 * 1000)
              );
              return {
                success: false,
                error: buildProfileCooldownMessage('Staff', hoursRemaining),
              };
            }
          }
        }
      }

      await setDoc(
        userRef,
        {
          ...data,
          ...(isStaffProfileUpdate
            ? { profileLastChangedAt: serverTimestamp() }
            : {}),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const approveDriver = async (driverId) => {
    try {
      await updateDoc(doc(db, 'drivers', driverId), {
        approved: true,
        status: 'approved',
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteDashboardUser = async (userId) => {
    try {
      if (!hasModuleAccess(user, 'createUser')) {
        return { success: false, error: 'You do not have permission to delete staff users.' };
      }

      if (!userId) {
        return { success: false, error: 'User ID is required.' };
      }

      if (user?.uid && userId === user.uid) {
        return { success: false, error: 'You cannot delete your own account.' };
      }

      const userRef = doc(db, 'users', userId);
      const userSnapshot = await getDoc(userRef);

      if (!userSnapshot.exists()) {
        return { success: false, error: 'Staff user not found.' };
      }

      const targetRole = normalizeRole(userSnapshot.data()?.role);
      if (targetRole === ROLES.ADMIN) {
        return { success: false, error: 'Admin users cannot be deleted from this panel.' };
      }

      await deleteDoc(userRef);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const rejectDriver = async (driverId, reason = '') => {
    try {
      await updateDoc(doc(db, 'drivers', driverId), {
        approved: false,
        status: 'rejected',
        rejectionReason: reason,
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const lockDriver = async (driverId, reason = '', options = {}) => {
    try {
      if (!isAdmin) {
        return { success: false, error: 'Only admin users can lock driver access.' };
      }

      const normalizedReason = String(reason || '').trim();
      const normalizedDocument = String(options.lockDocument || '').trim();
      const fallbackReason = normalizedDocument
        ? `Locked due to expired ${normalizedDocument}`
        : 'Locked by admin';

      await updateDoc(doc(db, 'drivers', driverId), {
        isLocked: true,
        lockReason: normalizedReason || fallbackReason,
        lockDocument: normalizedDocument,
        lockCategory: options.lockCategory || 'manual_lock',
        lockedAt: serverTimestamp(),
        lockedByUid: user?.uid || null,
        lockedByEmail: user?.email || '',
        unlockedAt: null,
        unlockedByUid: null,
        unlockedByEmail: null,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const unlockDriver = async (driverId) => {
    try {
      if (!isAdmin) {
        return { success: false, error: 'Only admin users can unlock driver access.' };
      }

      await updateDoc(doc(db, 'drivers', driverId), {
        isLocked: false,
        lockReason: '',
        lockDocument: '',
        lockCategory: '',
        unlockedAt: serverTimestamp(),
        unlockedByUid: user?.uid || null,
        unlockedByEmail: user?.email || '',
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const createDriver = async (driverData) => {
    try {
      const { password, confirmPassword, ...driverDataWithoutPassword } = driverData;
      const adminCreationMetadata = {
        createdByAdmin: true,
        createdByAdminUid: user?.uid || null,
        createdByAdminEmail: user?.email || '',
      };

      if (password && driverData.email) {
        try {
          const createdAuthUser = await createAuthUserAccount(driverData.email, password);
          await setDoc(doc(db, 'drivers', createdAuthUser.uid), {
            ...driverDataWithoutPassword,
            ...adminCreationMetadata,
            userId: createdAuthUser.uid,
            approved: false,
            status: 'pending',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          return { success: true, id: createdAuthUser.uid, message: 'Driver created with login credentials' };
        } catch (authError) {
          const docRef = await addDoc(collection(db, 'drivers'), {
            ...driverDataWithoutPassword,
            ...adminCreationMetadata,
            approved: false,
            status: 'pending',
            authError: authError.message,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          return {
            success: true,
            id: docRef.id,
            warning: `Driver profile created but login account failed: ${authError.message}`,
          };
        }
      }

      const docRef = await addDoc(collection(db, 'drivers'), {
        ...driverDataWithoutPassword,
        ...adminCreationMetadata,
        approved: false,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateDriver = async (driverId, data) => {
    try {
      const isDriverProfileUpdate = isProfileFieldUpdate(
        data,
        DRIVER_NON_PROFILE_UPDATE_FIELDS
      );
      const driverRef = doc(db, 'drivers', driverId);

      if (isDriverProfileUpdate) {
        const driverSnapshot = await getDoc(driverRef);
        const driverData = driverSnapshot.exists() ? driverSnapshot.data() : {};
        const lastProfileChange = parseTimestampToDate(
          driverData.profileLastChangedAt || driverData.lastProfileChangeAt
        );

        if (lastProfileChange) {
          const nextAllowedChangeAt =
            lastProfileChange.getTime() + PROFILE_CHANGE_COOLDOWN_MS;

          if (Date.now() < nextAllowedChangeAt) {
            const hoursRemaining = Math.ceil(
              (nextAllowedChangeAt - Date.now()) / (60 * 60 * 1000)
            );
            return {
              success: false,
              error: buildProfileCooldownMessage('Driver', hoursRemaining),
            };
          }
        }
      }

      await updateDoc(driverRef, {
        ...data,
        ...(isDriverProfileUpdate
          ? { profileLastChangedAt: serverTimestamp() }
          : {}),
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteDriver = async (driverId) => {
    try {
      await deleteDoc(doc(db, 'drivers', driverId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const createVehicle = async (vehicleData) => {
    try {
      const docRef = await addDoc(collection(db, 'warehouse'), {
        ...vehicleData,
        status: 'available',
        assignedDriver: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateVehicle = async (vehicleId, data) => {
    try {
      await updateDoc(doc(db, 'warehouse', vehicleId), { ...data, updatedAt: serverTimestamp() });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteVehicle = async (vehicleId) => {
    try {
      await deleteDoc(doc(db, 'warehouse', vehicleId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const createTask = async (taskData) => {
    try {
      if (taskData.vehicleId && taskData.vehicleSource === 'warehouse') {
        const vehicleRef = doc(db, 'warehouse', taskData.vehicleId);
        const vehicleSnapshot = await getDoc(vehicleRef);

        if (!vehicleSnapshot.exists()) {
          return {
            success: false,
            error: 'Selected fleet vehicle was not found. Please choose another vehicle.',
          };
        }

        const vehicleData = vehicleSnapshot.data() || {};
        const vehicleWithId = {
          id: vehicleSnapshot.id,
          ...vehicleData,
        };
        const assignedDriver = String(vehicleData.assignedDriver || '').trim();
        const requestedDriver = String(taskData.driverId || '').trim();
        const isTimeWindowAvailable = isVehicleReadyForAssignment(
          vehicleWithId,
          tasks,
          { lockHours: 4 }
        );
        const isAssignedToRequestedDriver =
          assignedDriver && requestedDriver && assignedDriver === requestedDriver;

        const canUseVehicle = isTimeWindowAvailable || isAssignedToRequestedDriver;

        if (!canUseVehicle) {
          return {
            success: false,
            error: 'Selected fleet vehicle is blocked by an active or overdue task. Please choose another vehicle.',
          };
        }

        await updateDoc(vehicleRef, {
          status: 'assigned',
          assignedDriver: taskData.driverId,
          updatedAt: serverTimestamp(),
        });
      }

      const docRef = await addDoc(collection(db, 'tasks'), {
        ...taskData,
        status: 'assigned',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateTask = async (taskId, data) => {
    try {
      await updateDoc(doc(db, 'tasks', taskId), { ...data, updatedAt: serverTimestamp() });
      if (data.status === 'completed' || data.status === 'cancelled') {
        const task = tasks.find((taskItem) => taskItem.id === taskId);
        if (task && task.vehicleId && task.vehicleSource === 'warehouse') {
          await updateDoc(doc(db, 'warehouse', task.vehicleId), {
            status: 'available',
            assignedDriver: null,
            updatedAt: serverTimestamp(),
          });
        }
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteTask = async (taskId) => {
    try {
      const task = tasks.find((taskItem) => taskItem.id === taskId);
      if (task && task.vehicleId && task.vehicleSource === 'warehouse') {
        await updateDoc(doc(db, 'warehouse', task.vehicleId), {
          status: 'available',
          assignedDriver: null,
          updatedAt: serverTimestamp(),
        });
      }
      await deleteDoc(doc(db, 'tasks', taskId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const addAvailability = async (availabilityData) => {
    try {
      const docRef = await addDoc(collection(db, 'availability'), {
        ...availabilityData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateAvailability = async (availabilityId, data) => {
    try {
      await updateDoc(doc(db, 'availability', availabilityId), { ...data, updatedAt: serverTimestamp() });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteAvailability = async (availabilityId) => {
    try {
      await deleteDoc(doc(db, 'availability', availabilityId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  return (
    <AdminContext.Provider
      value={{
        user,
        role: user?.role || null,
        modules: user?.modules || EMPTY_MODULES,
        loading,
        drivers,
        vehicles,
        tasks,
        availability,
        inspections,
        dashboardUsers,
        isAdmin,
        hasAccess,
        signIn,
        requestPasswordResetOtp,
        verifyPasswordResetOtp,
        sendPasswordResetEmail,
        signOut,
        createDashboardUser,
        saveUserPermissions,
        updateDashboardUser,
        deleteDashboardUser,
        approveDriver,
        rejectDriver,
        createDriver,
        updateDriver,
        deleteDriver,
        lockDriver,
        unlockDriver,
        createVehicle,
        updateVehicle,
        deleteVehicle,
        createTask,
        updateTask,
        deleteTask,
        addAvailability,
        updateAvailability,
        deleteAvailability,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
